import { Link } from 'react-router';
import { CollapsibleTable, StatStrip } from '@/features/org/overview/SectionCard';
import type { ReportPayload } from '@/lib/reports';
import { cn } from '@/lib/utils';

/**
 * Renders a computed report the way the email does: events first, then each section's figures as a strip and its
 * table collapsed to the first rows. Used by the run dialog, previews and the editor's review step.
 */
export function ReportPayloadView({ payload, compact = false }: { payload: ReportPayload; compact?: boolean }) {
  const who = [payload.subject.tenantName, payload.subject.jobName].filter(Boolean).join(' · ');
  return (
    <div className={cn('space-y-6', compact && 'space-y-5')}>
      <p className="text-muted-foreground text-xs">
        {who ? `${who} · ` : ''}
        {payload.window.label} ({payload.window.timezone})
      </p>
      {payload.isEmpty && <p className="bg-muted/50 border p-3 text-sm">Quiet window: nothing happened that this report tracks.</p>}
      {payload.events && payload.events.items.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold tracking-tight">
            What happened <span className="text-muted-foreground ml-1 text-xs font-normal">{payload.events.total} {payload.events.total === 1 ? 'event' : 'events'}</span>
          </h3>
          <ul className="divide-y border">
            {payload.events.items.map((e, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 text-sm">
                <span className="text-muted-foreground w-28 shrink-0 text-xs tabular-nums">{e.at}</span>
                <span className="font-medium">{e.path ? <Link to={e.path} className="hover:underline">{e.title}</Link> : e.title}</span>
                {e.detail && <span className="text-muted-foreground text-xs">{e.detail}</span>}
              </li>
            ))}
          </ul>
          {payload.events.total > payload.events.items.length && <p className="text-muted-foreground mt-1 text-xs">{payload.events.total - payload.events.items.length} more not shown.</p>}
        </section>
      )}
      {payload.sections.map((s) => {
        const hasTable = !!s.table && s.table.rows.length > 0;
        const hasStats = !!s.stats && s.stats.length > 0;
        return (
          <section key={s.key} className="border">
            <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
              <div>
                <h3 className="text-sm font-semibold tracking-tight">{s.title}</h3>
                {s.description && <p className="text-muted-foreground text-xs">{s.description}</p>}
              </div>
            </div>
            {hasStats && <StatStrip stats={s.stats!} className="px-4 pb-4" />}
            {hasTable ? (
              <div className="border-t px-1 pb-2">
                <CollapsibleTable table={s.table!} initial={compact ? 6 : 10} dense />
              </div>
            ) : (
              (s.empty || !hasStats) && <p className="text-muted-foreground px-4 pb-4 text-sm">{s.empty ?? 'Nothing in this window.'}</p>
            )}
          </section>
        );
      })}
    </div>
  );
}
